/**
 * Convert Esri REST JSON features into `SiteLayer` records for map overlay.
 */
import { computeLayerContentHash } from '../kmzParser';
import type { ArcgisCatalogEntry } from '../../config/arcgisLayerCatalog';
import type { SiteLayer, SiteLayerCategory } from '../../types/site';

export interface EsriFeature {
  attributes?: Record<string, unknown>;
  geometry?: EsriGeometry;
}

export type EsriGeometry =
  | { x?: number; y?: number }
  | { paths?: number[][][] }
  | { rings?: number[][][] }
  | { points?: number[][] };

export interface EsriFeatureResponse {
  features?: EsriFeature[];
  exceededTransferLimit?: boolean;
  error?: { message?: string; code?: number };
}

export interface ConvertOptions {
  catalogId: string;
  category: SiteLayerCategory;
  source: SiteLayer['source'];
  nameField?: string;
  nameFields?: string[];
  descriptionFields?: string[];
  /**
   * Source attributes to preserve verbatim on `SiteLayer.attributes`
   * (catalog `attributeFields`). Only primitive values survive; null /
   * undefined / empty-string attributes are dropped so downstream
   * readers never see fabricated placeholders.
   */
  attributeFields?: string[];
  /** Prefix for auto-generated names when attributes are empty. */
  labelPrefix?: string;
  sourceUrl?: string;
  dataAsOf?: string;
}

function asString(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = String(v).trim();
  return s.length > 0 ? s : undefined;
}

/**
 * Project the declared `attributeFields` onto a sparse primitive record
 * for `SiteLayer.attributes`. Returns undefined when nothing survives —
 * the field stays absent rather than carrying an empty object.
 */
function pickLayerAttributes(
  attrs: Record<string, unknown>,
  fields: string[] | undefined,
): Record<string, string | number | boolean | null> | undefined {
  if (!fields?.length) return undefined;
  const out: Record<string, string | number | boolean | null> = {};
  let any = false;
  for (const f of fields) {
    const v = attrs[f];
    if (v == null) continue;
    if (typeof v === 'number' || typeof v === 'boolean') {
      out[f] = v;
      any = true;
    } else if (typeof v === 'string' && v.trim().length > 0) {
      out[f] = v.trim();
      any = true;
    }
  }
  return any ? out : undefined;
}

function joinDescription(attrs: Record<string, unknown>, fields: string[] | undefined): string | undefined {
  if (!fields?.length) return undefined;
  const parts = fields
    .map(f => {
      const val = asString(attrs[f]);
      return val ? `${f}: ${val}` : undefined;
    })
    .filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function formatNameFieldValue(field: string, value: string): string | undefined {
  if (field === 'VOLTAGE' || field === 'MAX_VOLT' || field === 'MIN_VOLT') {
    const n = Number(value.replace(/[^\d.-]/g, ''));
    if (Number.isFinite(n) && n > 0) {
      const kv = n >= 1000 ? n / 1000 : n;
      return `${kv}${String(value).toLowerCase().includes('kv') ? '' : ' kV'}`;
    }
    // HIFLD unknown-voltage sentinel (-999999) — treat as absent rather
    // than naming the layer "-999999 kV".
    return undefined;
  }
  return value;
}

function buildFeatureName(
  attrs: Record<string, unknown>,
  opts: ConvertOptions,
  index: number,
  prefix: string,
): string {
  if (opts.nameFields?.length) {
    const parts = opts.nameFields
      .map(f => {
        const raw = asString(attrs[f]);
        return raw ? formatNameFieldValue(f, raw) : undefined;
      })
      .filter(Boolean);
    if (parts.length > 0) return parts.join(' · ');
  }
  if (opts.nameField) {
    const raw = asString(attrs[opts.nameField]);
    if (raw) {
      const formatted = formatNameFieldValue(opts.nameField, raw);
      if (formatted) return formatted;
    }
  }
  return `${prefix} feature ${index + 1}`;
}

/** Append segment id or sequence when multiple features share a label. */
function disambiguateFeatureNames(
  names: string[],
  features: EsriFeature[],
): string[] {
  const totals = new Map<string, number>();
  for (const name of names) {
    totals.set(name, (totals.get(name) ?? 0) + 1);
  }
  const seen = new Map<string, number>();
  return names.map((name, index) => {
    if ((totals.get(name) ?? 0) <= 1) return name;
    const seq = (seen.get(name) ?? 0) + 1;
    seen.set(name, seq);
    if (seq === 1) return name;
    const id = asString(features[index]?.attributes?.ID)
      ?? asString(features[index]?.attributes?.OBJECTID);
    if (id) return `${name} · ${id}`;
    return `${name} (${seq})`;
  });
}

/** Esri stores x=lng, y=lat in WGS84 queries with inSR=4326. */
function pointFromEsri(x: number, y: number): { lat: number; lng: number } {
  return { lat: y, lng: x };
}

function cleanRing(ring: number[][]): { lat: number; lng: number }[] {
  return ring
    .filter(pt => pt.length >= 2 && Number.isFinite(pt[0]) && Number.isFinite(pt[1]))
    .map(pt => pointFromEsri(pt[0], pt[1]));
}

/** Shoelace signed area in coordinate units — sign encodes winding only. */
function ringSignedArea(ring: { lat: number; lng: number }[]): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    sum += a.lng * b.lat - b.lng * a.lat;
  }
  return sum / 2;
}

/**
 * Keep the OUTER rings of a multipart polygon, dropping holes.
 *
 * The pre-2026-08 converter kept only `rings[0]` — every additional part
 * of a MultiPolygon (disjoint wetland cells, split habitat units, …)
 * silently vanished from the map. Winding convention differs between the
 * two feeds (Esri: outer=CW; GeoJSON via refdata: outer=CCW), so instead
 * of hardcoding one, outer is defined as "winds the same way as the
 * largest ring" — holes wind opposite their shell in both conventions.
 */
function outerRings(rings: number[][][]): { lat: number; lng: number }[][] {
  const cleaned = rings.map(cleanRing).filter(r => r.length >= 3);
  if (cleaned.length === 0) return [];
  if (cleaned.length === 1) return cleaned;
  const areas = cleaned.map(ringSignedArea);
  let largest = 0;
  for (let i = 1; i < areas.length; i++) {
    if (Math.abs(areas[i]) > Math.abs(areas[largest])) largest = i;
  }
  const outerSign = Math.sign(areas[largest]) || 1;
  const outers = cleaned.filter((_, i) => Math.sign(areas[i]) === outerSign);
  return outers.length > 0 ? outers : [cleaned[largest]];
}

/**
 * Convert one Esri geometry into ONE OR MORE SiteLayer geometries.
 *
 * Multi-part sources expand into parts instead of losing data:
 *   • `paths`  — one line per path (concatenating them drew phantom
 *     connector segments between unrelated pipeline branches);
 *   • `rings`  — one polygon per OUTER ring (previously all but the
 *     first ring were dropped).
 */
export function esriGeometryToSiteLayerGeometries(
  geometry: EsriGeometry | undefined,
): NonNullable<SiteLayer['geometry']>[] {
  if (!geometry) return [];

  if ('x' in geometry && 'y' in geometry) {
    const x = geometry.x;
    const y = geometry.y;
    if (typeof x !== 'number' || typeof y !== 'number') return [];
    if (!Number.isFinite(x) || !Number.isFinite(y)) return [];
    return [{ type: 'point', coordinates: [pointFromEsri(x, y)] }];
  }

  if ('points' in geometry && Array.isArray(geometry.points)) {
    const coords = geometry.points
      .filter(pt => pt.length >= 2 && Number.isFinite(pt[0]) && Number.isFinite(pt[1]))
      .map(pt => pointFromEsri(pt[0], pt[1]));
    if (coords.length === 0) return [];
    if (coords.length === 1) return [{ type: 'point', coordinates: coords }];
    return [{ type: 'line', coordinates: coords }];
  }

  if ('paths' in geometry && Array.isArray(geometry.paths)) {
    return geometry.paths
      .map(cleanRing)
      .filter(path => path.length >= 2)
      .map(coordinates => ({ type: 'line' as const, coordinates }));
  }

  if ('rings' in geometry && Array.isArray(geometry.rings)) {
    return outerRings(geometry.rings)
      .map(coordinates => ({ type: 'polygon' as const, coordinates }));
  }

  return [];
}

/** Single-geometry view — first part only. Kept for existing consumers. */
export function esriGeometryToSiteLayerGeometry(
  geometry: EsriGeometry | undefined,
): SiteLayer['geometry'] | null {
  return esriGeometryToSiteLayerGeometries(geometry)[0] ?? null;
}

/**
 * R-131-2 — Stable feature key for ArcGIS imports.
 *
 * Pre-R-131-2 this used `Date.now().toString(36)` as part of the id, so every
 * re-fetch of the same regional layer produced a brand-new id for the same
 * feature. The id-keyed Firestore subcollection write in
 * `saveSiteLayersBatch` then wrote a duplicate doc instead of an idempotent
 * upsert. Combined with `mergeImportedLayers` deduping only by `contentHash`,
 * any user rename made the next refetch resurrect the original under a new
 * id — the "creates duplicates" bug filed against Holly Ridge.
 *
 * Strategy: prefer an attribute-provided stable id (OBJECTID is the ArcGIS
 * convention, with FID/ID/GLOBALID as the documented fallbacks). When the
 * source doesn't surface one — older REST endpoints or sources we route
 * through PeeringDB — fall back to the canonical content hash, which is
 * deterministic per category + name + geometry. That fallback fails only
 * when a user later renames the layer locally; `mergeImportedLayers` covers
 * that case via its id-or-hash dedup pass.
 */
const STABLE_ID_ATTRS: readonly string[] = [
  'OBJECTID', 'ObjectId', 'objectid',
  'FID', 'fid',
  'GLOBALID', 'GlobalID', 'globalid',
  'ID', 'Id', 'id',
];

function stableFeatureKey(
  attrs: Record<string, unknown>,
  fallbackHash: string,
): string {
  for (const key of STABLE_ID_ATTRS) {
    const raw = asString(attrs[key]);
    if (raw) return raw.replace(/[^A-Za-z0-9._-]+/g, '_');
  }
  return `h-${fallbackHash}`;
}

function makeLayerId(catalogId: string, featureKey: string): string {
  return `arcgis-${catalogId}-${featureKey}`;
}

export function esriFeaturesToSiteLayers(
  features: EsriFeature[],
  opts: ConvertOptions,
): SiteLayer[] {
  const layers: SiteLayer[] = [];
  const now = new Date().toISOString();
  const prefix = opts.labelPrefix ?? opts.catalogId;

  const prepared: Array<{ feature: EsriFeature; geom: NonNullable<SiteLayer['geometry']>; name: string }> = [];
  features.forEach((feature, index) => {
    const geoms = esriGeometryToSiteLayerGeometries(feature.geometry)
      .filter(g => g.coordinates.length > 0);
    if (geoms.length === 0) return;
    const attrs = feature.attributes ?? {};
    const baseName = buildFeatureName(attrs, opts, index, prefix);
    geoms.forEach((geom, partIndex) => {
      prepared.push({
        feature,
        geom,
        // Part 0 keeps the bare name so single-part features (the vast
        // majority) produce byte-identical names/ids/hashes to the
        // pre-multipart converter; extra parts are labelled explicitly.
        name: partIndex === 0 ? baseName : `${baseName} · part ${partIndex + 1}`,
      });
    });
  });

  const names = disambiguateFeatureNames(
    prepared.map(p => p.name),
    prepared.map(p => p.feature),
  );

  // R-131-2 — collision-disambiguator. With the new stable id derivation
  // it's vanishingly rare for two features inside one fetch to share a
  // key (would require duplicate OBJECTIDs from the source), but the
  // fallback hash path COULD collide if two features have identical
  // category/name/geometry. Append `#N` in that case so per-doc writes
  // remain distinct rather than overwriting each other.
  const idSeen = new Map<string, number>();

  prepared.forEach(({ feature, geom }, index) => {
    const attrs = feature.attributes ?? {};
    const name = names[index] ?? `${prefix} feature ${index + 1}`;
    const description = joinDescription(attrs, opts.descriptionFields);

    const provisional: SiteLayer = {
      id: 'pending',
      name,
      category: opts.category,
      geometry: geom,
      description,
      source: opts.source,
      importedAt: now,
      visible: true,
    };
    const hash = computeLayerContentHash(provisional);
    const baseKey = stableFeatureKey(attrs, hash);
    const seq = idSeen.get(baseKey) ?? 0;
    idSeen.set(baseKey, seq + 1);
    const featureKey = seq === 0 ? baseKey : `${baseKey}-${seq}`;

    // Attributes attach AFTER the hash is computed — the content hash
    // contract stays "category + name + geometry (+description)" so
    // re-fetch dedup is unaffected by attribute presence.
    const preservedAttributes = pickLayerAttributes(attrs, opts.attributeFields);
    const layer: SiteLayer = {
      ...provisional,
      id: makeLayerId(opts.catalogId, featureKey),
      contentHash: hash,
      evidence: {
        use: opts.source === 'arcgis-import' || opts.source === 'peeringdb-import'
          ? 'screening'
          : opts.source === 'kmz-import' || opts.source === 'manual'
            ? 'site-truth'
            : 'map-only',
        source: {
          providerId: opts.catalogId,
          featureId: featureKey,
          sourceUrl: opts.sourceUrl,
          retrievedAt: now,
          dataAsOf: opts.dataAsOf,
        },
      },
      ...(preservedAttributes ? { attributes: preservedAttributes } : {}),
    };
    layers.push(layer);
  });

  return layers;
}

/**
 * RD-1 (E) — bridge a refdata GeoJSON feature into the Esri-feature shape
 * this module consumes, so refdata-served catalog fetches flow through the
 * SAME converter (identical ids, content hashes, evidence metadata —
 * re-fetches dedup across live↔refdata switches).
 */
export function refdataFeatureToEsriFeature(feature: {
  geom: { type: string; coordinates: unknown };
  props: Record<string, string | number | null>;
}): EsriFeature | null {
  const attributes = feature.props as EsriFeature['attributes'];
  switch (feature.geom.type) {
    case 'Point': {
      const [x, y] = feature.geom.coordinates as [number, number];
      return { attributes, geometry: { x, y } };
    }
    case 'MultiPoint': {
      const pts = feature.geom.coordinates as [number, number][];
      if (pts.length === 0) return null;
      return { attributes, geometry: { x: pts[0][0], y: pts[0][1] } };
    }
    case 'LineString':
      return { attributes, geometry: { paths: [feature.geom.coordinates as number[][]] } };
    case 'MultiLineString':
      return { attributes, geometry: { paths: feature.geom.coordinates as number[][][] } };
    case 'Polygon':
      return { attributes, geometry: { rings: feature.geom.coordinates as number[][][] } };
    case 'MultiPolygon':
      return { attributes, geometry: { rings: (feature.geom.coordinates as number[][][][]).flat() } };
    default:
      return null;
  }
}

export function catalogEntryToConvertOptions(
  entry: ArcgisCatalogEntry,
  source: SiteLayer['source'] = 'arcgis-import',
): Omit<ConvertOptions, 'catalogId'> & { catalogId: string } {
  return {
    catalogId: entry.id,
    category: entry.category,
    source: entry.kind === 'peeringdb' ? 'peeringdb-import' : source,
    nameField: entry.nameField,
    nameFields: entry.nameFields,
    descriptionFields: entry.descriptionFields,
    attributeFields: entry.attributeFields,
    labelPrefix: entry.label,
    sourceUrl: entry.queryUrl,
  };
}
